import dayjs from 'dayjs'
import { dateFormat, dateReadableFormats } from './date'
import {
  IMAGE_FILE_TYPES,
  IMAGE_EXTENSIONS,
  REGEXP_NOT_NUMBER,
  DISPLAYS,
  UTM_LIST,
  EMPTY_CELL_TABLE_TEXT,
  ENTITY_FIELD_TYPES,
  POSITIONS
} from './helpConsts'
import { post } from '../api/methods'
import { declinationWord, HOURS, MINUTES } from './words'
import { regexp } from '../plugins/veeValidate'
import { Mask } from 'maska'

export const routeLink = (url, blank = false) => {
  if (blank) {
    window.open(url)
    return
  }
  window.location.href = url
}

export const throttle = (callback, limit) => {
  let waiting = false
  return function () {
    if (!waiting) {
      callback.apply(this, arguments)
      waiting = true
      setTimeout(() => {
        waiting = false
      }, limit)
    }
  }
}

export const debounce = (func, timeout = 300) => {
  let timer
  const debounceFunction = (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
  debounceFunction.cancel = () => clearTimeout(timer)
  return debounceFunction
}

export const sizeWindow = {
  isMobile () {
    return window.innerWidth <= DISPLAYS.WIDE_SM
  },

  isTablet () {
    return window.innerWidth > DISPLAYS.WIDE_SM && window.innerWidth <= DISPLAYS.LG
  },

  isDesktop () {
    return window.innerWidth > DISPLAYS.LG
  },

  isMediaMaxMd () {
    return window.innerWidth <= DISPLAYS.MD
  }
}

/**
 * Забирает параметры из URL. Параметр может быть массивом
 * @param {string} [location = document.location]
 * @return {object}
 */
export const getParamsUrl = (location = document.location) => {
  const url = new URL(location)
  // TODO: предусмотреть объекты и вложенные структуры
  const arrayRegEx = /\[.*]/

  const params = {}
  url.searchParams.forEach((value, name) => {
    if (value === '') return

    const key = name.replace(arrayRegEx, '')
    if (arrayRegEx.test(name) || params.hasOwnProperty(key)) {
      params[key] = Array.isArray(params[key]) ? [...params[key], value] : [value]
    } else {
      params[key] = value
    }
  })
  return params
}
/**
 * Создаёт URLSearchParams структуру с заданными параметрами.
 * Параметр может быть массивом
 * @param {object} params
 * @return {URLSearchParams}
 */
export const getUrlSearchParams = (params) => {
  const searchParams = new URLSearchParams()
  for (const key in params) {
    const value = params[key]
    if (isEmpty(value)) continue

    // TODO: предусмотреть объекты
    if (Array.isArray(value)) {
      value.forEach(valueItem => searchParams.append(`${key}[]`, valueItem))
    } else {
      searchParams.set(key, value)
    }
  }
  return searchParams
}

/**
 * Заменяет все параметры url
 * @param {object} params - Параметры
 * @param {boolean} [isPushHistory=false] - Записать изменения в историю
 */
export const setParamsUrl = (params, isPushHistory = false) => {
  const urlSearchParams = getUrlSearchParams(params)

  const url = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + urlSearchParams.toString()
  if (isPushHistory) {
    window.history.pushState({ path: url }, '', url)
    return
  }
  window.history.replaceState({ path: url }, '', url)
}

/**
 * Добавляет параметры в url. Если параметр существует - перезаписывает его
 * @param {object} params - Параметр
 * @param {boolean} [isPushHistory=false] - Записать изменения в историю
 */
export const pushParamsUrl = (params, isPushHistory = false) => {
  const searchParams = getParamsUrl()
  setParamsUrl({ ...searchParams, ...params }, isPushHistory)
}

/**
 *
 * @param {string[]} removedParams - удаляемые параметры
 @param {boolean} [isPushHistory=false] - Записать изменения в историю
 */

export const removeParamsUrl = (removedParams, isPushHistory = false) => {
  const searchParams = getParamsUrl()

  for (const param in searchParams) {
    removedParams.forEach(removedParam => {
      delete searchParams[removedParam]
    })
  }

  setParamsUrl(searchParams, isPushHistory)
}

export const hasOwnProperty = (obj, key) => {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

export const currency = (value, emptyText = 'Не указано', showDigits = false) => {
  return value > 0
    ? `${value.toLocaleString('ru-RU', {
    currency: 'RUB',
    minimumFractionDigits: showDigits ? 2 : null
  })} ₽`
    : emptyText
}

export const redirectUrl = (url) => {
  window.location.assign(url)
}

export const findElementWithClass = (el, className) => {
  while (el.parentNode) {
    if (typeof el.className === 'string' && el.className.indexOf(className) !== -1) { return true }
    el = el.parentNode
  }
  return false
}

export const getPaymentStatus = (item) => {
  if (item?.status?.stage === 'RESERVATION') {
    if (item.reservation_confirm) {
      return {
        text_color: '#000',
        bg_color: '#00b547',
        value: item.reservation_price ? 'Оплачена' : 'Подтверждена'
      }
    } else {
      return {
        text_color: '#fff',
        bg_color: '#f14848',
        value: item.reservation_price ? 'Не оплачено' : 'Не подтверждено'
      }
    }
  }
}

export const deepClone = obj => {
  if (!isObjectType(obj)) return obj
  const clone = Object.assign({}, obj)

  Object.keys(clone).forEach(
    key =>
      (clone[key] =
              typeof obj[key] === 'object' ? deepClone(obj[key]) : obj[key])
  )

  return Array.isArray(obj) && obj.length
    ? (clone.length = obj.length) && Array.from(clone)
    : Array.isArray(obj)
      ? Array.from(obj)
      : clone
}

export const firstUpper = (string) => {
  if (!string) return
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const getCellTable = (value, emptyText = EMPTY_CELL_TABLE_TEXT, canEmpty) => {
  if (canEmpty) return
  return value || emptyText
}

const EMPTY_VALUES = ['', undefined, null, NaN]
/**
 * Проверка на пустое значение
 * @param value - проверяемое значение
 * @param {boolean} [checkStringNull = false] - считать строку 'null' за пустое значение
 * @return {boolean}
 */
export const isEmpty = (value, checkStringNull = false) => {
  if (Array.isArray(value) && !value.length) return true
  return EMPTY_VALUES.includes(value) || (checkStringNull && (value === 'null'))
}

export const isObject = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export const isObjectType = (value) => {
  return value !== null && typeof value === 'object'
}

// Заменяет массивы
export const mergeDeepObject = (mainObject, secondaryObject) => {
  if (!isObject(secondaryObject)) {
    return isObjectType(secondaryObject) ? deepClone(secondaryObject) : secondaryObject
  }
  return Object.entries(mainObject).reduce((acc, curr) => {
    const [key, mainValue] = curr
    if (!hasOwnProperty(secondaryObject, key)) {
      acc[key] = isObjectType(mainValue)
        ? deepClone(mainValue)
        : mainValue
      return acc
    }
    const secondaryValue = secondaryObject[key]

    if (isObject(mainValue) && isObject(secondaryValue)) {
      acc[key] = mergeDeepObject(mainValue, secondaryValue)
      return acc
    }

    acc[key] = isObjectType(mainValue)
      ? deepClone(secondaryValue)
      : secondaryValue
    return acc
  }, {})
}

export const getFormatPhone = (phone) => {
  if (!phone) return
  if (!regexp.tel.test(phone)) return phone

  const RAW_PHONE_LENGTH_WITH_CODE = 11
  let numberPhone = getNumberValue(phone)

  if (numberPhone.length >= RAW_PHONE_LENGTH_WITH_CODE) {
    numberPhone = numberPhone.replace(/^(8|7)/, '')
  }

  const mask = new Mask({ mask: '+7 (###) ###-##-##' })
  return mask.masked(numberPhone)
}

export const getHouseNameOrAddress = (house) => {
  return house.address || house.name
}

export const getUserFullName = (user) => {
  if (!user) return
  const nameList = []
  if (user.last_name) nameList.push(user.last_name)
  if (user.first_name) nameList.push(user.first_name)
  if (user.middle_name) nameList.push(user.middle_name)

  return nameList.join(' ')
}

// TODO: переименовать
export const getUserShortFullName = (user) => {
  if (!user) return
  return `${user.last_name} ${user.first_name}`
}

/**
 * Возвращает инициалы
 * @param args {string}
 * @returns {string}
 */
export const getInitial = (...args) => {
  return args.reduce((resultString, argString) => {
    return argString ? `${resultString}${argString.charAt(0).toUpperCase()}.` : resultString
  }, '')
}

export const getUserFullNameInitial = user => {
  if (!user) return
  const { last_name: lastName, first_name: firstName, middle_name: middleName } = user
  if (lastName) return `${lastName} ${getInitial(firstName, middleName)}`
  if (firstName && middleName) return `${firstName} ${middleName}`
  return firstName || middleName || ''
}

export const getUsersFullName = (users, isWithInitial) => {
  return users.map(isWithInitial ? getUserFullNameInitial : getUserFullName).join(', ')
}

export const getUsersShortFullName = (users) => {
  return users.map(getUserShortFullName).join(', ')
}

export const truncateText = (string, maxLength) => {
  if (string?.length > maxLength) {
    return string.slice(0, maxLength) + '...'
  } else {
    return string
  }
}

export const getMapByKey = (array, key = 'id') => {
  if (!Array.isArray(array)) return new Map()
  return array?.reduce((acc, cur) => acc.set(cur[key], cur), new Map())
}

export const getMapByStringKey = (array, key = 'id') => {
  if (!Array.isArray(array)) return new Map()
  return array?.reduce((acc, cur) => acc.set(String(cur[key]), cur), new Map())
}

export const getSetByArrayField = (array, field = 'id') => {
  if (!Array.isArray(array)) return
  return array.reduce((acc, cur) => acc.add(cur?.[field]), new Set())
}

export const getObjectByKey = (array, key = 'id') => {
  return array.reduce((object, item) => {
    object[item[key]] = item
    return object
  }, {})
}

export const groupByKey = (array, key) => {
  return array.reduce((object, item) => {
    const itemKey = [item[key]]
    if (object[itemKey]) object[itemKey].push(item)
    else object[itemKey] = [item]
    return object
  }, {})
}

export const getObjectToArray = (obj, isNumberId = false) => {
  return Object.entries(obj).map(([id, value]) => ({ id: isNumberId ? +id : id, value }))
}

export const isSet = (set) => String(set) === '[object Set]'

export const isMap = (map) => String(map) === '[object Map]'

export const isSubstring = (text, searchValue) => {
  const innerText = String(text ?? '').toLowerCase()
  const innerSearchValue = (searchValue ?? '').toLowerCase()
  return innerText.indexOf(innerSearchValue) !== -1
}

export const getCustomFieldName = (item, type) => {
  return `customField[${item.code}][${type || 'like'}]`
}

export const getPageHeight = () => {
  return Math.max(
    document.body.scrollHeight, document.documentElement.scrollHeight,
    document.body.offsetHeight, document.documentElement.offsetHeight,
    document.body.clientHeight, document.documentElement.clientHeight
  )
}

export const getPageWidth = () => {
  return Math.max(
    document.body.scrollWidth, document.documentElement.scrollWidth,
    document.body.offsetWidth, document.documentElement.offsetWidth,
    document.body.clientWidth, document.documentElement.clientWidth
  )
}

/**
 * Возвращает расстояние до конца страницы.
 * Если элемент находится в фиксированном контейнере, возвращает расстояние до конца контейнера.
 * @param el
 * @return {number}
 */
export const getSizeToEndPage = (el) => {
  // TODO: Проверить, уместен ли тут такой функционал
  const parents = getParents(el)
  const fixedParent = parents.find(parent => getComputedStyle(parent)?.position === 'fixed')

  const elTopHeight = el.getBoundingClientRect().bottom

  if (fixedParent) return Math.floor(fixedParent.scrollHeight - fixedParent.scrollTop - elTopHeight)

  const pageHeight = getPageHeight()
  const scrollHeight = window.pageYOffset
  return Math.floor(pageHeight - scrollHeight - elTopHeight)
}

/**
 * Возвращает позицию элемента относительно верхней точки документа
 * @param el
 * @return {{top: number, left: number, bottom: number, right: number}}
 */
export const getPosition = el => {
  const box = el.getBoundingClientRect()

  return {
    top: box.top + window.pageYOffset,
    right: box.right + window.pageXOffset,
    bottom: box.bottom + window.pageYOffset,
    left: box.left + window.pageXOffset
  }
}

/**
 * Проверка наличия скрола у элемента
 * @param el {HTMLElement}
 * @return {boolean}
 */

export const hasScroll = (el) => {
  if (!el) return false
  return el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
}

/**
 * Получить массив всех родительских DOM элементов
 * @param el
 * @return {HTMLElement[]}
 */

export const getParents = (el) => {
  const parents = []

  let innerElement = el

  while (innerElement?.parentElement) {
    innerElement = innerElement?.parentElement
    if (innerElement) {
      parents.push(innerElement)
    }
  }

  return parents
}

export const hexToRgba = (hex, opacity) => {
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16))
  return `rgba(${r},${g},${b},${opacity})`
}

export const getFieldsFromArray = (array, field = 'id') => {
  return array?.map(item => item[field]) ?? []
}

export const isImageFile = file => {
  const lowerCaseType = file.type?.toLowerCase()
  return IMAGE_FILE_TYPES.includes(lowerCaseType)
}

export const isImageExtension = extension => {
  const lowerCaseExtension = extension.toLowerCase()
  return IMAGE_EXTENSIONS.includes(lowerCaseExtension)
}

export const getExtensionByName = fileName => {
  return fileName?.split('.').pop()
}

export const getNameWithoutExtension = fileName => {
  return fileName?.replace(/(.\w+)$/, '')
}

export const readFile = file => {
  return new Promise((res, rej) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (result === 'data:') rej(new Error('file is empty'))
      res(result)
    }
    reader.onerror = () => {
      rej(reader.error)
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Удаляет из значения все не числовые символы.
 * Меняет запятые на точку.
 * Пример работы: --1-23abc456,1,2 => -123456.12
 * @param {string|number} number
 * @returns {string}
 */
export const getNumberValue = number => {
  const numberValue = String(number)
    .replace(/,/g, '.')
    .replace(REGEXP_NOT_NUMBER, '')
    .replace(/-/g, (match, offset) => {
      if (offset > 0) return ''
      return match
    })
  return replaceSubsequentOccurrences(numberValue, '.')
}

/**
 * Ставит разделители (пробел) между разрядами числа
 * @param {string|number} number
 * @returns {string}
 */
export const splitNumberByDigit = number => {
  return String(number).replace(/(\d)(?=(\d{3})+([^\d]|$))/g, '$1 ')
}

/**
 * Приводит значение к числу и разделяет по разрядам через пробел.
 * Пример работы: --1-23abc456,1,2 => -123 456.12
 * @param {string|number} number
 * @returns {string}
 */
export const formatNumber = number => {
  const numberValue = getNumberValue(number)
  return splitNumberByDigit(numberValue)
}

/**
 * Подсчитывает количество вхождений подстроки в строке
 * @param {string} text
 * @param {string} substring
 * @returns {number}
 */
export const getSubstringCount = (text, substring) => {
  return text.split(substring).length - 1
}

/**
 * Удаляет пробелы
 * @param {string} text
 * @returns {string}
 */
export const removeSpaces = text => {
  return text.replace(/\s/g, '')
}

/**
 * Оставляет только первое вхождение подстроки в строку
 * @param {string|number} text
 * @param {string|RegExp} substring
 * @returns {string}
 */
export const replaceSubsequentOccurrences = (text, substring) => {
  let hasSubstring = false
  return String(text).replaceAll(substring, match => {
    if (hasSubstring) return ''
    hasSubstring = true
    return match
  })
}

export const uniqueArray = (array) => {
  if (!array) return
  return array.filter((value, index, self) => self.indexOf(value) === index)
}

/**
 * Проверяет, является ли значение файлом
 * @param value
 * @return {boolean}
 */
export const isFile = value => {
  return (value instanceof File) || (value instanceof Blob)
}

/**
 * Возвращает FormData из object. Мутирует параметр formData
 * @param {Object} data
 * @param {FormData} [formData]
 * @param {String|null} [parentKey = null]
 * @return {FormData}
 */
export const getFormData = (data, formData = new FormData(), parentKey = null) => {
  if (isEmpty(data)) return formData
  return Object.entries(data).reduce((formData, [key, value]) => {
    if (value === null || value === undefined) return formData
    const innerKey = parentKey ? `${parentKey}[${key}]` : key

    if (isObjectType(value) && !(value instanceof Date) && !isFile(value)) {
      getFormData(value, formData, innerKey)
    } else {
      formData.append(innerKey, value)
    }
    return formData
  }, formData)
}

export const isActivePath = (pathname) => {
  const activeUrl = new URL(window.location)
  return activeUrl.pathname === pathname
}

/**
 * Проверяет, присутствует ли в текущем url параметр со значением
 * @param name
 * @param value
 * @return {boolean}
 */
export const includeParamsValue = (name, value) => {
  const activeUrl = new URL(window.location)
  return activeUrl.searchParams.get(name) === value
}

export const getMonthlyPayment = (sum, percent, years) => {
  const months = years * 12

  const percentPerMonth = percent / 12 / 100
  const totalRate = (1 + percentPerMonth) ** months

  const monthlyPayment = (sum * percentPerMonth * totalRate) / (totalRate - 1)
  return +monthlyPayment.toFixed(2)
}

export const getTimeFromMinutes = (minutes) => {
  const hours = Math.trunc(minutes / 60)
  const remainderMinutes = minutes % 60
  const hoursText = hours ? `${hours} ${declinationWord(hours, HOURS)}` : ''
  const minutesText = remainderMinutes ? `${remainderMinutes} ${declinationWord(remainderMinutes, MINUTES)}` : ''
  return `${hoursText} ${minutesText}`
}

export const getFlatArrayByField = (data, field) => {
  return data.reduce((acc, cur) => {
    const children = cur[field]

    acc.push(cur)
    if (children?.length) acc.push(...getFlatArrayByField(children, field))
    return acc
  }, [])
}

export const exportFile = async (url) => {
  const res = await post(null, null, { url })
  const file = res.data.file
  if (isEmpty(file)) return
  routeLink(`/tempFiles/${file}`)
}

export const booleanToNumber = (value) => {
  return value ? 1 : 0
}

export const getUpdateProxy = (object, callback) => {
  return new Proxy(deepClone(object), {
    set: (target, prop, value, receiver) => {
      target[prop] = value
      callback?.(receiver)
      return true
    }
  })
}

/**
 * Создаёт глубокий прокси. Не реагирует на добавление новых глубоко вложенных объектов
 * @param object
 * @param callback
 * @param {boolean} [isNeedUpdate=true] - изменять значение
 * @return {any}
 */
export const getDeepProxy = (object, callback, isNeedUpdate = true) => {
  const parents = new WeakMap()
  const getItemPath = (item) => {
    const parent = parents.get(item)
    return parent ? [...getItemPath(parent.target), parent.property] : []
  }

  const getProxy = object => {
    return new Proxy(object, {
      get (target, property, receiver) {
        const item = Reflect.get(target, property, receiver)
        if (isObject(item)) {
          parents.set(item, { target, property })
          return getProxy(item, callback)
        }
        return item
      },
      set (target, property, newValue, receiver) {
        if (isNeedUpdate) target[property] = newValue
        callback(target, property, newValue, receiver, getItemPath(target))
        return true
      }
    })
  }

  return getProxy(object)
}

export const printElement = async (el) => {
  window.dispatchEvent(new Event('beforeprint'))

  const htmlBody = document.body
  const printElementData = el.cloneNode(true)
  const htmlHead = document.head.innerHTML

  const iframe = document.createElement('iframe')
  iframe.setAttribute('id', 'print_frame')

  await new Promise(res => {
    iframe.addEventListener('load', res)
    htmlBody.append(iframe)
  })

  const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
  const iframeBody = iframeDocument.body

  iframeDocument.head.innerHTML = htmlHead
  iframeBody.append(printElementData)

  setTimeout(() => {
    const canvasList = el.querySelectorAll('canvas')
    printElementData.querySelectorAll('canvas').forEach((canvas, index) => {
      const realCanvas = canvasList[index]
      canvas.style.height = `${realCanvas.clientHeight}px`
      canvas.style.width = `${realCanvas.clientWidth}px`
      canvas.height = realCanvas?.height
      canvas.width = realCanvas?.width
      canvas.getContext('2d').drawImage(canvasList[index], 0, 0)
    })
    iframe.contentWindow.print()
    window.dispatchEvent(new Event('afterprint'))

    iframe.remove()
  }, 200)
}

/**
 * Создаёт задержку
 * @param time время в ms
 * @return {Promise<void>}
 */
export const sleep = async (time = 0) => {
  return new Promise(res => setTimeout(res, time))
}

/**
 * Возвращает число либо пустую строку
 * @param value
 * @return {number|string}
 */
export const getNumberOrEmptyString = (value) => {
  return parseInt(value) || ''
}

export const getUTM = () => {
  return UTM_LIST.reduce((utm, key) => {
    const value = localStorage.getItem(key)
    if (value) utm[key] = value
    return utm
  }, {})
}

/**
 * Заменяет свойства target на поля из data
 * @param {object} data - объект с данными
 * @param {object} target - мутируемый объект
 * @param {Boolean} [skipNull = false] - не записывать null
 */
export const mutateObject = (data, target, skipNull = false) => {
  Object.entries(data).forEach(([key, value]) => {
    if (!hasOwnProperty(target, key) || (skipNull && data[key] === null)) return

    if (isObject(value)) mutateObject(value, target[key])
    else target[key] = value
  })
}

/**
 * Мутирует исходный объект, заполняя его данными. Возвращает результат.
 * @param {object} initObject - Исходный объект
 * @param {object} data - Данные
 * @return {object}
 */
export const fillObject = (initObject, data) => {
  for (const key in initObject) {
    initObject[key] = isEmpty(data?.[key]) ? initObject[key] : data?.[key]
  }
  return initObject
}

/**
 * Возвращает дату в часах
 * @param hours {number} - часы
 * @return {string} - строка вида '5 дн. 3 ч.'
 */
export const getDaysFromHours = (hours) => {
  const DAY = 24
  if (hours < DAY) {
    return `${hours} ч.`
  }
  const day = Math.floor(hours / DAY)
  const divHours = hours % DAY
  return `${day} дн. ${divHours ? ` ${divHours} ч.` : ''}`
}

/**
 * Сравнивает даты и возвращает одну из них
 * @param dateA
 * @param dateB
 * @param {boolean} [returnEarliest=true] - Вернуть более реннюю или позднюю дату
 * @return {*}
 */
export const compareDate = (dateA, dateB, returnEarliest = true) => {
  if (!dateA) return dateB
  if (!dateB) return dateA

  const isLateB = dayjs(dateA).isAfter(dateB)

  if (returnEarliest) return isLateB ? dateB : dateA
  return isLateB ? dateA : dateB
}

/**
 * Возвращает большую из дат
 * @param dateA
 * @param dateB
 * @return {*}
 */
export const getLateDate = (dateA, dateB) => {
  if (!dateA) return dateB
  if (!dateB) return dateA
  return dayjs(dateA).isAfter(dateB) ? dateA : dateB
}

/**
 * Возвращает меньшую из дат
 * @param dateA
 * @param dateB
 * @return {*}
 */
export const getEarlyDate = (dateA, dateB) => {
  if (!dateA) return dateB
  if (!dateB) return dateA
  return dayjs(dateA).isAfter(dateB) ? dateB : dateA
}

/**
 * Вычисляет процент выполненных работ
 * @param fact
 * @param plan
 * @return {number}
 */
export const getCompletionPercentage = (plan, fact) => {
  if (!fact || !plan) return 0
  return Math.round((fact / plan) * 100)
}

/**
 * Генерирует случайное целое число в интервале
 * @param min
 * @param max
 * @return {number}
 */
export const getRandomNumber = (min, max) => {
  const random = min + Math.random() * (max + 1 - min)
  return Math.floor(random)
}

/**
 * Сортирует массив по полю позиция на месте
 * @param array
 */
export const sortByObjectPosition = (array) => {
  array.sort((objectA, objectB) => objectA.position - objectB.position)
}

/**
 * Возвращает текст с изменённой первой буквой на строчную
 * @param {string} text
 * @return {string}
 */
export const toFirstLetter = text => {
  if (!text) return
  return `${text[0].toLowerCase() + text.slice(1)}`
}

/**
 * join поля из списка объектов
 * @param {Object[]} array
 * @param {string} fieldName
 * @param {string} separator
 * @return {*}
 */
export const joinField = (array, fieldName, separator) => {
  return array.map(obj => obj?.[fieldName]).join(separator)
}

export const hasFocus = (el) => {
  if (!el) return
  return el.contains(document.activeElement)
}

/**
 * Определяет iOS
 * @link https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
 * @return {boolean}
 */
export const isIos = () => {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
}

/**
 * Форматирует в человекопонятный формат значение поля сущности
 * @param value - Форматируемое значение
 * @param {string} type - Тип значение
 * @param {boolean} subtractTimezone - показывать таймзону в utc
 * @return {string|*}
 */
export const formatValue = (value, type) => {
  switch (type) {
    case ENTITY_FIELD_TYPES.DATE:
      return dateFormat(value, dateReadableFormats.date, true)
    case ENTITY_FIELD_TYPES.DATETIME:
      return dateFormat(value, dateReadableFormats.dateTime, false)
    case ENTITY_FIELD_TYPES.INTEGER:
    case ENTITY_FIELD_TYPES.FLOAT:
      return formatNumber(value)
    default:
      return value
  }
}

const getDropdownWidth = (container, padding = 15) => {
  const position = getPosition(container)
  const pageWidth = getPageWidth()

  return {
    [POSITIONS.RIGHT]: pageWidth - position.left - padding,
    [POSITIONS.LEFT]: position.right - padding,
    [POSITIONS.CENTER]: (Math.min(pageWidth - position.right, position.left) - padding) * 2 + container.offsetWidth
  }
}

/**
 * Возвращает положение выпадающего окна относительно родителя
 * @param {HTMLElement} container
 * @param {number} height
 * @param {number} maxWidth
 * @param {number} minWidth
 * @param {number} [elementMargin=1] - отступ дропдауна относительно родителя
 * @return {{x: string, y: string, maxWidth: number, minWidth: number}}
 */
export const getDropdownPosition = (container, height, maxWidth, minWidth, elementMargin = 1) => {
  const bottomSize = getSizeToEndPage(container)
  const containerPosition = getPosition(container)

  let positionX = POSITIONS.RIGHT
  const positionY = bottomSize <= (height + elementMargin) ? POSITIONS.TOP : POSITIONS.BOTTOM
  let innerMaxWidth = maxWidth

  if ((containerPosition.right + maxWidth) > getPageWidth()) {
    const dropdownWidth = getDropdownWidth(container)

    if ((containerPosition.left - maxWidth) >= 0) {
      positionX = POSITIONS.LEFT
    } else {
      for (const key in dropdownWidth) {
        if (dropdownWidth[key] > dropdownWidth[positionX]) positionX = key
      }
    }

    innerMaxWidth = maxWidth <= dropdownWidth[positionX] ? maxWidth : dropdownWidth[positionX]
  }

  const innerMinWidth = Math.max(
    Math.min(minWidth, innerMaxWidth),
    container?.offsetWidth
  )

  return {
    x: positionX,
    y: positionY,
    maxWidth: innerMaxWidth,
    minWidth: innerMinWidth
  }
}

/**
 * Возвращает ближайшее справа число из массива
 * @param {Object[]} array - Массив поиска
 * @param {number} value - Исходное число
 * @return {number}
 */
export const getNearestValueInArray = (array = [], value = 0) => {
  return array.reduce(
    (nearest, item) => Math.abs(item - value) >= Math.abs(nearest - value) && nearest < item ? nearest : item,
    value
  )
}

/**
 * Возвращает объект с координатами клика
 * @param {Event} e - Событие
 * @return {*}
 */
export const getClientCoordinateFromTouchEvent = (e) => {
  if (!e) return
  const touch = e.touches?.[0]
  return {
    clientX: touch.clientX,
    clientY: touch.clientY
  }
}

/**
 * Возвращает булевое значение, закешилось изображение или нет
 * @param {string} imageSrc - src изображения
 * @return {boolean}
 */
export const isImageCached = (imageSrc) => {
  const image = new Image()
  image.src = imageSrc
  return image.complete
}

/**
 * Записывает текст в буфер
 * @param text
 * @return {Promise<void>}
 */
export const writeClipboardText = async text => {
  return await navigator.clipboard.writeText(text)
}

/**
 * проверяем кликабельный ли элемент
 * @param {Event} e - событие
 * @return {boolean}
 */
export const isElementClickable = (e) => {
  const isClickable = e.target?.getAttribute('data-clickable')
  if (isEmpty(isClickable)) return true
  return isClickable === 'true'
}

/**
 * Возвращает массив элементов, отсутствующих в list
 * @param {*[]} comparable
 * @param {*[]} list
 * @return {*[]}
 */
export const arrayDifference = (comparable, list) => {
  if (!Array.isArray(list)) return comparable
  if (!Array.isArray(comparable)) return []
  const listSet = new Set(list)
  return comparable.filter(embed => !listSet.has(embed))
}

export const getCurrency = (value) => {
  return `${value.toLocaleString('ru-RU', { currency: 'RUB' })} ₽`
}

export const shaderColor = (color, percent) => {
  const num = parseInt(color.slice(1), 16)
  const amt = Math.round(2.55 * percent)
  const R = (num >> 16) + amt
  const G = (num >> 8 & 0x00FF) + amt
  const B = (num & 0x0000FF) + amt
  return ('#' + (0x1000000 +
      (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1))
}

export const formatToKebabCase = text => {
  return text.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

/**
 * Возвращает vue computed объект для работы с sync свойствами
 * @param fieldName - имя поля в camelCase, для которого будет созданно sync свойство
 * @param prefix - префикс в событии update, с которым будет выполняться изменение свойства
 * @returns {{set(*): void, get(): *}}
 */
export const getVueSync = function (fieldName, prefix = 'inner') {
  const kebabCaseFieldName = formatToKebabCase(fieldName)
  return {
    get () {
      return this[fieldName]
    },
    set (value) {
      this.$emit(`update:${kebabCaseFieldName}`, value)
    }
  }
}

/**
 * Объединяет значения в общий массив. Если значение и так является массивом, разворачивает его
 * @param valueA
 * @param valueB
 * @returns {*[]}
 */
export const mergeToArray = (valueA, valueB) => {
  const array = Array.isArray(valueA) ? [...valueA] : [valueA]
  return Array.isArray(valueB)
    ? [...array, ...valueB]
    : [...array, valueB]
}
